import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { reactionAPI } from '../services/api';
import { reactionDuelSocket } from '../services/socket';
import { buildInviteLink } from '../utils/inviteLinks';
import QuickChat from '../components/QuickChat';
import '../styles/Flappy.css';
import '../styles/Reaction.css';

const DEFAULT_ROUND_MS = 60_000;
const TRAP_CHANCE = 0.15;

const COLORS = [
  { key: 'blue', label: 'Blue', hex: '#3b82f6' },
  { key: 'green', label: 'Green', hex: '#10b981' },
  { key: 'yellow', label: 'Yellow', hex: '#facc15' },
  { key: 'orange', label: 'Orange', hex: '#f97316' },
  { key: 'white', label: 'White', hex: '#ffffff' },
  { key: 'pink', label: 'Pink', hex: '#ec4899' },
  { key: 'purple', label: 'Purple', hex: '#a855f7' },
  { key: 'red', label: 'Red', hex: '#ef4444' }
];

const COLOR_BY_KEY = Object.fromEntries(COLORS.map((c) => [c.key, c]));
const ALL_COLOR_KEYS = COLORS.map((c) => c.key);

function buildOptionKeys(correctKey) {
  const pool = ALL_COLOR_KEYS.filter((k) => k !== correctKey);
  const decoys = shuffleArray(pool).slice(0, 3);
  const options = [correctKey, ...decoys].filter(Boolean);
  const unique = Array.from(new Set(options));
  while (unique.length < 4 && pool.length) {
    const next = pool.find((k) => !unique.includes(k));
    if (!next) break;
    unique.push(next);
  }
  const shuffled = shuffleArray(unique).slice(0, 4);
  if (correctKey && !shuffled.includes(correctKey)) {
    shuffled[shuffled.length - 1] = correctKey;
  }
  return shuffled;
}

function asNumber(n) {
  const x = Number(n);
  return Number.isFinite(x) ? x : 0;
}

function getMultiplier(streak) {
  if (streak >= 9) return 4;
  if (streak >= 6) return 3;
  if (streak >= 3) return 2;
  return 1;
}

function pickDifferentIndex(n, notIndex) {
  if (n <= 1) return 0;
  let idx = Math.floor(Math.random() * n);
  while (idx === notIndex) idx = Math.floor(Math.random() * n);
  return idx;
}

function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function ReactionMatch() {
  const navigate = useNavigate();
  const { matchId } = useParams();
  const { user, refreshUser } = useAuth();

  const [match, setMatch] = useState(null);
  const [roundData, setRoundData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [rematching, setRematching] = useState(false);
  const [acceptingRematch, setAcceptingRematch] = useState(false);
  const [pendingRematchId, setPendingRematchId] = useState(null);

  // Local Color Confusion run state
  const [gameStatus, setGameStatus] = useState('ready'); // ready | running | done
  const [timeLeftMs, setTimeLeftMs] = useState(DEFAULT_ROUND_MS);
  const [promptWordIdx, setPromptWordIdx] = useState(0);
  const [promptInkIdx, setPromptInkIdx] = useState(1);
  const [correctKey, setCorrectKey] = useState(null);
  const [optionKeys, setOptionKeys] = useState([]);
  const [score, setScore] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [wrong, setWrong] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [isTrap, setIsTrap] = useState(false);
  const [locked, setLocked] = useState(false);
  const [lastResult, setLastResult] = useState(null); // 'correct' | 'wrong' | null

  const intervalRef = useRef(null);
  const endAtRef = useRef(null);
  const startedAtRef = useRef(null);
  const lastRoundKeyRef = useRef(null);
  const pendingSubmissionRef = useRef(null);

  const [nowTick, setNowTick] = useState(() => Date.now());
  const hasAutoResumedRef = useRef(false);
  const [hasPendingSubmission, setHasPendingSubmission] = useState(false);
  const inviteJoinAttemptedRef = useRef(false);

  const getRunStorageKey = () => `reaction:run:${matchId}:${asNumber(user?.id)}:${asNumber(match?.current_round || 1)}`;
  const getPendingSubmitKey = () => `reaction:pending-submit:${matchId}:${asNumber(user?.id)}:${asNumber(match?.current_round || 1)}`;

  const getSubmitWindowRemainingMs = () => {
    if (!match?.started_at) return asNumber(match?.duration_ms || DEFAULT_ROUND_MS) || DEFAULT_ROUND_MS;
    const startedAt = new Date(match.started_at).getTime();
    if (Number.isNaN(startedAt)) return asNumber(match?.duration_ms || DEFAULT_ROUND_MS) || DEFAULT_ROUND_MS;
    const windowMs = (asNumber(match?.turn_timeout_seconds) || 300) * 1000;
    return Math.max(0, startedAt + windowMs - Date.now());
  };

  const cleanupInterval = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = null;
  };

  const nextPrompt = () => {
    const wordIdx = Math.floor(Math.random() * COLORS.length);
    const inkIdx = pickDifferentIndex(COLORS.length, wordIdx);
    setPromptWordIdx(wordIdx);
    setPromptInkIdx(inkIdx);

    const trap = Math.random() < TRAP_CHANCE;
    setIsTrap(trap);

    let nextCorrectKey = trap ? COLORS[wordIdx]?.key : COLORS[inkIdx]?.key;
    if (!nextCorrectKey) {
      nextCorrectKey = ALL_COLOR_KEYS[Math.floor(Math.random() * ALL_COLOR_KEYS.length)];
    }
    setCorrectKey(nextCorrectKey);
    setOptionKeys(buildOptionKeys(nextCorrectKey));
  };

  const load = async () => {
    try {
      setError('');
      const mRes = await reactionAPI.getMatch(matchId);
      const m = mRes.data;
      setMatch(m);

      const meIdLocal = asNumber(user?.id);
      if (m?.status === 'completed' && meIdLocal) {
        try {
          const otherPlayerId =
            meIdLocal === asNumber(m.creator_id)
              ? asNumber(m.opponent_id)
              : asNumber(m.creator_id);

          const cRes = await reactionAPI.listChallenges();
          const list = cRes.data || [];
          const rematch = list.find((item) => (
            asNumber(item.creator_id) === otherPlayerId &&
            asNumber(item.opponent_id) === meIdLocal &&
            item.status === 'pending' &&
            !item.accepted_at
          ));
          setPendingRematchId(rematch ? asNumber(rematch.id) : null);
        } catch {
          setPendingRematchId(null);
        }
      } else if (m?.status !== 'completed') {
        setPendingRematchId(null);
      }

      const roundNum = asNumber(m.current_round || 1);
      const rRes = await reactionAPI.getRound(matchId, roundNum);
      setRoundData(rRes.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load match');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 2000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId]);

  useEffect(() => {
    if (!matchId) return;
    reactionDuelSocket.join(matchId);

    const onUpdate = async (payload = {}) => {
      try {
        const updated = payload?.match;
        if (!updated) return;
        if (String(updated.id) !== String(matchId)) return;
        setMatch(updated);

        const roundNum = asNumber(updated.current_round || 1);
        const rRes = await reactionAPI.getRound(matchId, roundNum);
        setRoundData(rRes.data);
      } catch {
        // ignore
      }
    };

    const onRematchCreated = (payload = {}) => {
      try {
        if (String(payload?.originalMatchId) !== String(matchId)) return;
        const incomingRematchId = asNumber(payload?.newMatchId);
        const intendedOpponentId = asNumber(payload?.opponentId);
        if (!incomingRematchId) return;
        if (intendedOpponentId !== asNumber(user?.id)) return;
        setPendingRematchId(incomingRematchId);
      } catch {
        // ignore
      }
    };

    reactionDuelSocket.onMatchUpdate(onUpdate);
    reactionDuelSocket.onRematchCreated(onRematchCreated);

    return () => {
      reactionDuelSocket.removeListeners();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId, user?.id]);

  useEffect(() => {
    refreshUser?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => cleanupInterval(), []);

  useEffect(() => {
    hydratePendingSubmission();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId, user?.id, match?.current_round]);

  const meId = asNumber(user?.id);
  const creatorId = asNumber(match?.creator_id);
  const opponentId = asNumber(match?.opponent_id);
  const isCreator = meId > 0 && creatorId > 0 && meId === creatorId;
  const isOpponent = meId > 0 && opponentId > 0 && meId === opponentId;

  const currentRound = asNumber(match?.current_round || 1);
  const runs = roundData?.runs || [];
  const myRun = runs.find((r) => asNumber(r.player_id) === meId);

  const isParticipant = !!(meId && (meId === creatorId || meId === opponentId));
  const matchStatus = String(match?.status || '');
  const canJoinOpenRoom = matchStatus === 'pending' && !match?.opponent_id && !isCreator && !isOpponent;

  const persistRunState = () => {
    try {
      if (!match || !meId) return;
      if (matchStatus !== 'in_progress' || !isParticipant || myRun) {
        if (myRun || matchStatus === 'completed' || matchStatus === 'cancelled') {
          localStorage.removeItem(getRunStorageKey());
          localStorage.removeItem(getPendingSubmitKey());
          pendingSubmissionRef.current = null;
          setHasPendingSubmission(false);
        }
        return;
      }
      if (gameStatus !== 'running' && gameStatus !== 'done') return;
      const payload = {
        gameStatus,
        timeLeftMs,
        promptWordIdx,
        promptInkIdx,
        correctKey,
        optionKeys,
        score,
        correct,
        wrong,
        streak,
        bestStreak,
        isTrap,
        startedAtMs: startedAtRef.current,
        endAtMs: endAtRef.current,
        savedAt: Date.now(),
      };
      localStorage.setItem(getRunStorageKey(), JSON.stringify(payload));
    } catch {
      // ignore
    }
  };

  const savePendingSubmission = (payload) => {
    try {
      pendingSubmissionRef.current = payload;
      localStorage.setItem(getPendingSubmitKey(), JSON.stringify(payload));
      setHasPendingSubmission(true);
    } catch {
      // ignore
    }
  };

  const clearPendingSubmission = () => {
    try {
      localStorage.removeItem(getPendingSubmitKey());
    } catch {
      // ignore
    }
    pendingSubmissionRef.current = null;
    setHasPendingSubmission(false);
  };

  const hydratePendingSubmission = () => {
    try {
      const raw = localStorage.getItem(getPendingSubmitKey());
      if (!raw) {
        pendingSubmissionRef.current = null;
        setHasPendingSubmission(false);
        return;
      }
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') {
        clearPendingSubmission();
        return;
      }
      pendingSubmissionRef.current = parsed;
      setHasPendingSubmission(true);
    } catch {
      pendingSubmissionRef.current = null;
      setHasPendingSubmission(false);
    }
  };

  useEffect(() => {
    if (match?.status !== 'in_progress' || !match?.started_at) return undefined;
    const id = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [match?.status, match?.started_at]);

  useEffect(() => {
    const roundKey = `${matchId}:${currentRound}`;
    const lastRoundKey = lastRoundKeyRef.current;
    const isFirstSeen = lastRoundKey == null;
    const roundChanged = !!lastRoundKey && lastRoundKey !== roundKey;

    if (!isFirstSeen && !roundChanged) return;

    setSubmitted(false);
    setSubmitting(false);
    hasAutoResumedRef.current = false;
    cleanupInterval();
    endAtRef.current = null;
    startedAtRef.current = null;
    setGameStatus('ready');
    const nextRoundMs = asNumber(match?.duration_ms || DEFAULT_ROUND_MS) || DEFAULT_ROUND_MS;
    setTimeLeftMs(nextRoundMs);
    setScore(0);
    setCorrect(0);
    setWrong(0);
    setStreak(0);
    setBestStreak(0);
    setIsTrap(false);
    setLocked(false);
    setLastResult(null);
    setCorrectKey(null);
    setOptionKeys([]);

    lastRoundKeyRef.current = roundKey;
  }, [matchId, currentRound, match?.duration_ms]);

  const startTurn = () => {
    cleanupInterval();
    setSubmitted(false);
    setSubmitting(false);
    setGameStatus('running');
    const submitWindowRemaining = getSubmitWindowRemainingMs();
    const runDurationMs = asNumber(match?.duration_ms || DEFAULT_ROUND_MS) || DEFAULT_ROUND_MS;
    const nextRoundMs = Math.max(0, Math.min(runDurationMs, submitWindowRemaining));
    if (nextRoundMs <= 0) {
      setTimeLeftMs(0);
      setLocked(true);
      setGameStatus('done');
      persistRunState();
      return;
    }
    setTimeLeftMs(nextRoundMs);
    setScore(0);
    setCorrect(0);
    setWrong(0);
    setStreak(0);
    setBestStreak(0);
    setIsTrap(false);
    setLocked(false);
    setLastResult(null);
    setCorrectKey(null);
    startedAtRef.current = Date.now();
    endAtRef.current = Date.now() + nextRoundMs;
    nextPrompt();
    persistRunState();
    intervalRef.current = setInterval(() => {
      const left = Math.max(0, (endAtRef.current ?? Date.now()) - Date.now());
      setTimeLeftMs(left);
      if (left <= 0) {
        cleanupInterval();
        setLocked(true);
        setGameStatus('done');
        persistRunState();
      }
    }, 120);
  };

  useEffect(() => {
    if (matchStatus !== 'in_progress') return;
    if (!isParticipant) return;
    if (myRun) return;
    if (gameStatus !== 'ready') return;
    if (hasAutoResumedRef.current) return;
    hasAutoResumedRef.current = true;
    try {
      const raw = localStorage.getItem(getRunStorageKey());
      if (!raw) return;
      const saved = JSON.parse(raw);
      const endAtMs = Number(saved?.endAtMs || 0);
      const now = Date.now();
      const submitWindowRemaining = getSubmitWindowRemainingMs();
      if (!Number.isFinite(endAtMs) || endAtMs <= 0 || submitWindowRemaining <= 0) {
        localStorage.removeItem(getRunStorageKey());
        return;
      }
      const timeLeft = Math.max(0, Math.min(endAtMs - now, submitWindowRemaining));

      setPromptWordIdx(asNumber(saved?.promptWordIdx));
      setPromptInkIdx(asNumber(saved?.promptInkIdx));
      setCorrectKey(saved?.correctKey || null);
      setOptionKeys(Array.isArray(saved?.optionKeys) ? saved.optionKeys : []);
      setScore(asNumber(saved?.score));
      setCorrect(asNumber(saved?.correct));
      setWrong(asNumber(saved?.wrong));
      setStreak(asNumber(saved?.streak));
      setBestStreak(asNumber(saved?.bestStreak));
      setIsTrap(Boolean(saved?.isTrap));
      setLocked(false);
      startedAtRef.current = Number(saved?.startedAtMs || now);
      endAtRef.current = now + timeLeft;
      if (timeLeft <= 0 || saved?.gameStatus === 'done') {
        setTimeLeftMs(0);
        setGameStatus('done');
        setLocked(true);
      } else {
        setTimeLeftMs(timeLeft);
        setGameStatus('running');
        intervalRef.current = setInterval(() => {
          const left = Math.max(0, (endAtRef.current ?? Date.now()) - Date.now());
          setTimeLeftMs(left);
          if (left <= 0) {
            cleanupInterval();
            setLocked(true);
            setGameStatus('done');
          }
        }, 120);
      }
    } catch {
      // ignore
    }
  }, [matchStatus, isParticipant, myRun, gameStatus, match?.started_at, match?.duration_ms]);

  useEffect(() => {
    persistRunState();
  }, [
    gameStatus, timeLeftMs, promptWordIdx, promptInkIdx,
    correctKey, optionKeys, score, correct, wrong, 
    streak, bestStreak, isTrap, matchStatus, isParticipant, myRun
  ]);

  const handleAnswer = (choiceKey) => {
    if (gameStatus !== 'running') return;
    if (locked) return;
    if (timeLeftMs <= 0) return;
    setLocked(true);
    const expectedKey = correctKey || (isTrap ? COLORS[promptWordIdx]?.key : COLORS[promptInkIdx]?.key);
    const isCorrect = choiceKey === expectedKey;
    setLastResult(isCorrect ? 'correct' : 'wrong');
    if (isCorrect) {
      const nextStreak = streak + 1;
      const mult = getMultiplier(nextStreak);
      setScore((s) => s + (2 * mult));
      setCorrect((c) => c + 1);
      setStreak(nextStreak);
      setBestStreak((b) => (nextStreak > b ? nextStreak : b));
    } else {
      setScore((s) => s - 3);
      setWrong((w) => w + 1);
      setStreak(0);
    }
    window.setTimeout(() => {
      setLastResult(null);
      nextPrompt();
      setLocked(false);
    }, 140);
  };

  const handleSubmit = async (overridePayload = null) => {
    if (!match || !isParticipant || submitted || submitting || myRun) return;
    if (!overridePayload && gameStatus !== 'done') return;
    try {
      setSubmitting(true);
      setError('');
      const startedAt = Number(overridePayload?.startedAt || startedAtRef.current || Date.now());
      const roundMs = asNumber(match?.duration_ms || DEFAULT_ROUND_MS) || DEFAULT_ROUND_MS;
      const durationMs = Math.max(0, Math.min(roundMs, Number(overridePayload?.durationMs ?? (Date.now() - startedAt))));
      const correctCount = asNumber(overridePayload?.correctCount ?? correct);
      const wrongCount = asNumber(overridePayload?.wrongCount ?? wrong);
      const attempts = asNumber(overridePayload?.attempts ?? (correctCount + wrongCount));
      const totalScore = asNumber(overridePayload?.score ?? (score + accuracyBonus));
      await reactionAPI.submitRun(match.id, currentRound, { score: totalScore, correctCount, wrongCount, attempts, durationMs });
      setSubmitted(true);
      localStorage.removeItem(getRunStorageKey());
      clearPendingSubmission();
      refreshUser?.();
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to submit run');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRematch = async () => {
    if (!match || !isParticipant || matchStatus !== 'completed' || rematching) return;
    try {
      setRematching(true);
      setError('');
      const res = await reactionAPI.rematch(match.id);
      refreshUser?.();
      navigate(`/reaction/duel/${res.data.id}`);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to create rematch');
    } finally {
      setRematching(false);
    }
  };

  const handleAcceptRematch = async () => {
    const nextId = asNumber(pendingRematchId);
    if (!nextId || acceptingRematch) return;
    try {
      setAcceptingRematch(true);
      setError('');
      await reactionAPI.acceptMatch(nextId);
      refreshUser?.();
      navigate(`/reaction/duel/${nextId}`);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to accept rematch');
    } finally {
      setAcceptingRematch(false);
    }
  };

  useEffect(() => {
    if (!correctKey) return;
    if (!optionKeys.length || !optionKeys.includes(correctKey)) {
      setOptionKeys(buildOptionKeys(correctKey));
    }
  }, [correctKey, optionKeys]);

  useEffect(() => {
    const tryAutoJoinFromInvite = async () => {
      if (!canJoinOpenRoom || inviteJoinAttemptedRef.current) return;
      inviteJoinAttemptedRef.current = true;
      try {
        await reactionAPI.joinMatch(match.id);
        await load();
        refreshUser?.();
      } catch (e) {
        const msg = String(e?.response?.data?.error || '').toLowerCase();
        if (msg.includes('full') || msg.includes('joined') || e?.response?.status === 409) {
          setError('This room is full. You can watch the game live.');
        } else {
          setError(e.response?.data?.error || 'Unable to join this room');
        }
      }
    };
    tryAutoJoinFromInvite();
  }, [canJoinOpenRoom, match?.id, refreshUser]);

  const attemptsTotal = correct + wrong;
  const accuracy = attemptsTotal > 0 ? correct / attemptsTotal : 0;
  const accuracyBonus = attemptsTotal >= 5 && accuracy >= 0.9 ? Math.round(accuracy * 20) : 0;
  
  useEffect(() => {
    if (!match || match.status !== 'in_progress' || !isParticipant || myRun || submitted || submitting) return;
    if (hasPendingSubmission) { handleSubmit(pendingSubmissionRef.current); return; }
    if (gameStatus !== 'done') return;
    handleSubmit();
  }, [gameStatus, match, isParticipant, myRun, submitted, submitting, hasPendingSubmission]);

  useEffect(() => {
    if (!match || match.status !== 'in_progress' || !isParticipant || myRun || submitted || submitting || gameStatus !== 'done') return;
    const startedAt = startedAtRef.current || Date.now();
    const roundMs = asNumber(match?.duration_ms || DEFAULT_ROUND_MS) || DEFAULT_ROUND_MS;
    savePendingSubmission({
      startedAt,
      durationMs: Math.max(0, Math.min(roundMs, Date.now() - startedAt)),
      score: score + accuracyBonus,
      correctCount: correct,
      wrongCount: wrong,
      attempts: correct + wrong,
    });
  }, [gameStatus, match, isParticipant, myRun, submitted, submitting, score, correct, wrong]);

  if (loading) return <div className="flappy-page"><div className="flappy-hint">Loading...</div></div>;
  if (!match) return <div className="flappy-page"><div className="flappy-error">Match not found</div><button className="flappy-btn" onClick={() => navigate('/reaction/duel')}>Back to Lobby</button></div>;

  const badge = (() => {
    if (matchStatus === 'pending') {
      if (!match.opponent_id) return 'Waiting for opponent to join...';
      if (!match.accepted_at) return isOpponent ? 'Challenge received' : 'Waiting for opponent to accept...';
      return isCreator ? 'Ready to start' : 'Waiting for creator to start...';
    }
    if (matchStatus === 'completed') return 'Match completed';
    return 'In progress';
  })();

  const canCancel = matchStatus === 'pending' && isCreator && !match.started_at;
  const promptWord = COLORS[promptWordIdx]?.label ?? '�';
  const promptInk = COLORS[promptInkIdx]?.hex ?? '#ffffff';
  const promptInkKey = COLORS[promptInkIdx]?.key;
  const timeLeftSec = Math.ceil(timeLeftMs / 1000);

  const windowLeftSec = (() => {
    if (!match?.started_at) return null;
    const startedAt = new Date(match.started_at).getTime();
    if (Number.isNaN(startedAt)) return null;
    const windowMs = (asNumber(match.turn_timeout_seconds) || 300) * 1000;
    return Math.max(0, Math.ceil((startedAt + windowMs - nowTick) / 1000));
  })();

  const roundCompleted = roundData?.round?.status === 'completed' || matchStatus === 'completed';
  const bothSubmitted = (roundData?.runs || []).length >= 2;
  const roundWinnerText = (() => {
    const winnerId = asNumber(roundData?.round?.winner_id);
    if (!winnerId) return 'Tie';
    if (winnerId === meId) return 'You';
    return 'Opponent';
  })();

  const payoutSummary = (() => {
    const wager = asNumber(match?.wager || 0);
    if (!wager) return null;
    const grossPot = wager * 2;
    const chargePercent = Number.isFinite(Number(match?.charge_percent)) ? Number(match.charge_percent) : 7;
    const chargeAmount = Number.isFinite(Number(match?.charge_amount)) ? Number(match.charge_amount) : Math.floor((grossPot * chargePercent) / 100);
    const netPot = Number.isFinite(Number(match?.net_pot)) ? Number(match.net_pot) : Math.max(0, grossPot - chargeAmount);
    return { grossPot, chargePercent, chargeAmount, netPot };
  })();

  const inviteLink = match?.id ? buildInviteLink(`/reaction/duel/${match.id}`) : '';
  const copyInviteLink = async () => {
    if (!inviteLink) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(inviteLink);
      } else {
        window.prompt('Copy invite link:', inviteLink);
      }
    } catch {
      window.prompt('Copy invite link:', inviteLink);
    }
  };

  return (
    <div className="flappy-page reaction-duel">
      <div className="flappy-header">
        <div>
          <h1 className="flappy-title">{'🎨'} Color Confusion Duel &bull; Match #{match.id}</h1>
          <div className="flappy-subtitle">{badge} &bull; Round {currentRound}/{match.rounds_total}</div>
        </div>
        <div className="flappy-header-actions">
          <button className="flappy-btn" onClick={() => navigate('/reaction/duel')}>&larr; Lobby</button>
          <button className="flappy-btn" onClick={load}>Refresh</button>
        </div>
      </div>

      {error && <div className="flappy-error">{error}</div>}

      <div className="flappy-grid">
        <section className="flappy-card">
          <h2>Match</h2>
          <div className="flappy-meta">
            <div><strong>Wager:</strong> {match.wager}</div>
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div className="player-badge">
                <span className="player-name">{match.creator_username}</span>
                <span className="cm-player-stat cm-player-stat--rating">P {match.creator_rating || 1200}</span>
                {asNumber(match.creator_streak) >= 3 && <span className="cm-player-stat cm-player-stat--streak">=% {match.creator_streak}</span>}
              </div>
              <div className="player-badge">
                <span className="player-name">{match.opponent_username || 'Waiting...'}</span>
                {match.opponent_id && (
                  <>
                    <span className="cm-player-stat cm-player-stat--rating">P {match.opponent_rating || 1200}</span>
                    {asNumber(match.opponent_streak) >= 3 && <span className="cm-player-stat cm-player-stat--streak">=% {match.opponent_streak}</span>}
                  </>
                )}
              </div>
            </div>
          </div>
          
          {inviteLink && (
            <div className="match-invite-row" style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                className="flappy-btn-primary"
                onClick={copyInviteLink}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9rem', padding: '6px 12px' }}
                title="Copy Invite Link to clipboard"
              >
                =� Copy Invite Link
              </button>
            </div>
          )}

          {matchStatus === 'completed' && (
            <div className="flappy-hint" style={{ marginTop: 10 }}>
              Result: <strong>{match.winner_id ? (match.winner_id === meId ? 'You Won' : 'Opponent Won') : 'Tie'}</strong>
              {payoutSummary && ` " Net Payout: ${payoutSummary.netPot}`}
            </div>
          )}

          {matchStatus === 'completed' && (
            <div style={{ marginTop: '0.75rem', display: 'flex', gap:10 }}>
               {pendingRematchId ? (
                <button className="flappy-btn-primary" onClick={handleAcceptRematch} disabled={acceptingRematch}>
                  {acceptingRematch ? 'Accepting...' : 'Accept Rematch'}
                </button>
               ) : (
                <button className="flappy-btn-primary" onClick={handleRematch} disabled={rematching}>
                  {rematching ? 'Creating...' : 'Rematch'}
                </button>
               )}
            </div>
          )}

          {canCancel && (
            <button className="flappy-btn" style={{ marginTop: 10 }} onClick={async () => {
              try {
                setError('');
                await reactionAPI.cancelMatch(match.id);
                refreshUser?.();
                navigate('/reaction/duel');
              } catch (e) {
                setError(e.response?.data?.error || 'Failed to cancel challenge');
              }
            }}>
              Cancel Challenge
            </button>
          )}

          {matchStatus === 'pending' && match.opponent_id && !match.accepted_at && isOpponent && (
            <button className="flappy-btn-primary" style={{ marginTop: 10 }} onClick={async () => {
              try {
                setError('');
                await reactionAPI.acceptMatch(match.id);
                await load();
              } catch (e) {
                setError(e.response?.data?.error || 'Failed to accept challenge');
              }
            }}>
              Accept Challenge
            </button>
          )}

          {matchStatus === 'pending' && match.opponent_id && match.accepted_at && !match.started_at && isCreator && (
            <button className="flappy-btn-primary" style={{ marginTop: 10 }} onClick={async () => {
              try {
                setError('');
                await reactionAPI.startMatch(match.id);
                await load();
              } catch (e) {
                setError(e.response?.data?.error || 'Failed to start match');
              }
            }}>
              Start Match
            </button>
          )}
        </section>

        <section className="flappy-card">
          <h2>Round {currentRound}/{match.rounds_total}</h2>

          {/* Show pending state messages inside game panel */}
          {matchStatus === 'pending' && (
            <div className="flappy-hint" style={{ marginTop: 10, textAlign: 'center' }}>
              {!match.opponent_id && '� Waiting for an opponent to join...'}
              {match.opponent_id && !match.accepted_at && isOpponent && '=K You have been challenged! Accept below to start.'}
              {match.opponent_id && !match.accepted_at && !isOpponent && '� Waiting for opponent to accept your challenge...'}
              {match.opponent_id && match.accepted_at && !match.started_at && isCreator && ' Opponent accepted! Click Start Match below.'}
              {match.opponent_id && match.accepted_at && !match.started_at && !isCreator && '� Waiting for the host to start the match...'}
            </div>
          )}

          {matchStatus === 'completed' && !myRun && (
            <div className="flappy-hint" style={{ marginTop: 10, textAlign: 'center' }}>This match has ended.</div>
          )}

          {matchStatus === 'in_progress' && (
          <div className="color-game-arena">
             <div className="arena-stats">
               <div>Time: {timeLeftSec}s</div>
               <div>Score: {score}</div>
               <div>Streak: {streak}</div>
             </div>

             <div className="prompt-container">
               {gameStatus === 'running' ? (
                 <div className="prompt-word" style={{ color: promptInk }}>{promptWord}
                    {isTrap && <span className="trap-hint"> ⚠️</span>}
                  </div>
               ) : (
                 <div className="prompt-ready">{gameStatus === 'done' ? 'Time!' : 'Get Ready!'}</div>
               )}
             </div>

             <div className="options-grid">
               {gameStatus === 'running' ? (
                 optionKeys.map(key => (
                   <button key={key} className="option-btn" onClick={() => handleAnswer(key)} disabled={locked}>
                     {COLOR_BY_KEY[key]?.label}
                   </button>
                 ))
               ) : (
                 <button
                   className="start-btn"
                   onClick={startTurn}
                   disabled={gameStatus === 'done' || submitted || !!myRun}
                 >
                   {myRun ? 'Submitted ' : gameStatus === 'done' ? 'Waiting...' : 'START'}
                 </button>
               )}
             </div>
          </div>
          )}

          {(roundCompleted || bothSubmitted) && (
            <div className="round-summary" style={{ marginTop: 12 }}>
               <h3>Results</h3>
               {roundData?.runs?.map(r => (
                 <div key={r.player_id}>{r.username}: {r.score} pts</div>
               ))}
            </div>
          )}
        </section>
      </div>

      <QuickChat socket={reactionDuelSocket.socket} roomId={match.id} meId={user?.id} gameType="reaction" />
    </div>
  );
}



